/**
 * Export deliveries to CSV based on currently displayed data.
 * Each line item becomes a separate row in the CSV.
 */

const CSV_HEADERS = [
  'Name',
  'Suburb',
  'State',
  'Post Code',
  'Street',
  'Street No',
  'Unit No',
  'Mobile',
  'Email',
  'DOC_TYPE',
  'Customer No',
  'Customer Reference No',
  'SKU',
  'Facility',
  'Qty',
  'REQ_DATE_H',
  'Delivery Type',
  'Installation',
  'SERIAL_NO',
  'Return Reason',
  'Delivery Notes',
  'Direction notes',
];

/**
 * Escape a value for CSV output.
 * Wraps in double quotes if the value contains commas, quotes, or newlines.
 */
function escapeCsvValue(value) {
  if (value === null || value === undefined) {
    return '';
  }

  const str = String(value);
  if (str.includes(',') || str.includes('"') || str.includes('\n') || str.includes('\r')) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
}

/**
 * Convert a single line item to a CSV row array.
 * Accepts an optional parent delivery so order-level fields (name, address,
 * customerReference, etc.) can fall back to the grouped parent when they are
 * missing from the individual line item.
 */
function lineItemToRow(item, parent) {
  const p = parent || {};
  return [
    item.name ?? p.name,
    item.suburb ?? p.suburb,
    item.state ?? p.state,
    item.postCode ?? p.postCode,
    item.street ?? p.street,
    item.streetNo ?? p.streetNo,
    item.unitNo ?? p.unitNo,
    item.mobile ?? p.mobile,
    item.email ?? p.email,
    item.docType ?? p.docType,
    item.customerNo ?? p.customerNo,
    item.customerReference ?? p.customerReference,
    item.sku,
    item.facility,
    item.qty,
    item.reqDate ?? p.reqDate,
    item.deliveryType,
    item.installation,
    item.serialNo,
    item.returnReason,
    item.deliveryNotes,
    item.directionNotes ?? p.directionNotes,
  ];
}

/**
 * Convert grouped deliveries to CSV string.
 * Flattens grouped orders so each line item is its own row.
 */
export function deliveriesToCsv(deliveries) {
  const rows = [];

  for (const delivery of deliveries) {
    if (delivery.lineItems && delivery.lineItems.length > 0) {
      for (const item of delivery.lineItems) {
        rows.push(lineItemToRow(item, delivery));
      }
    } else {
      // Single item (ungrouped)
      rows.push(lineItemToRow(delivery));
    }
  }

  const headerLine = CSV_HEADERS.map(escapeCsvValue).join(',');
  const dataLines = rows.map((row) => row.map(escapeCsvValue).join(','));

  return [headerLine, ...dataLines].join('\r\n');
}

/**
 * Trigger a CSV file download in the browser.
 */
export function downloadCsv(csvContent, filename = 'orders-export.csv') {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();

  // Cleanup
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Export currently displayed deliveries as a CSV download.
 */
export function exportOrdersCsv(deliveries) {
  const csv = deliveriesToCsv(deliveries);
  const timestamp = new Date().toISOString().slice(0, 10);
  downloadCsv(csv, `orders-export-${timestamp}.csv`);
}
